/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';
import Router from 'next/router';
import styled, { css, ThemeProvider } from 'styled-components';
import withRedux from 'next-redux-wrapper';

import styles from '@jobcase/react_pattern_library/styles';
import rebrandStyles from '@jobcase/react_pattern_library/styles/rebrand';

import GlobalToasterNotification from '../components/GlobalToasterNotification';
import GlobalToasterNotificationRebrand from '../components/GlobalToasterNotification/GlobalToasterNotificationRebrand';
import Head from '../components/Head';

import { getOrganization } from '../redux/nodes/features/settings/actions';
import { loadAllEmployerAccounts, loadEmployerAccount } from '../redux/nodes/features/employers/actions';
import { selectIsRebrand } from '../redux/nodes/utils/selectors';
import { setHistory, popHistory, PAGES } from '../redux/nodes/router/actions';
import { setRebrand } from '../redux/nodes/utils/actions';
import applicationActions, { getAgreementAcceptStatus } from '../redux/nodes/application/actions';
import authenticationActions from '../redux/nodes/authentication/actions';
import employerActions from '../redux/nodes/entities/employers/actions';
import makeStore from '../redux/store';

import { getCookie } from '../utils/cookie';
import { openAppStoreIfNeeded } from '../utils/device';
import { useIsMobile } from '../utils/hooks';
import Analytics from '../hoc/Analytics';
import AppContext from '../utils/context';
import config from '../config';
import { getCurrentProfile, getIsAuthenticated } from '../redux/nodes/authentication/selectors';
import useFullstory from '../utils/useFullstory';
import { JOBCASE_EMPLOYER_ID } from '../utils/constants/employerNavigation';

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  const whyDidYouRender = require('@welldone-software/why-did-you-render');

  whyDidYouRender(React, {
    onlyLogs: true,
    titleColor: 'green',
    diffNameColor: 'aqua',
    logOnDifferentValues: true,
  });
}

const PageContainer = styled.div`
  min-height: 100%;

  ${({ isRebrand }) => (isRebrand && css`
    background-color: ${rebrandStyles.color.backgroundGrayColor};
  `)}
`;

const client = new ApolloClient({
  uri: `${config.apiUrl}/v1/revenue-reports/graphql`,
  cache: new InMemoryCache(),
  credentials: 'include',
});

const handleRouteChangeComplete = () => {
  window.scrollTo(0, 0);
};

let isBackClick = false;

const PageContainerWrapper = ({ Component, pageProps, router }) => {
  const isRebrand = useSelector(selectIsRebrand);
  useFullstory();
  const Notification = isRebrand ? GlobalToasterNotificationRebrand : GlobalToasterNotification;
  const profile = useSelector(getCurrentProfile);
  const isAuthenticated = useSelector(getIsAuthenticated);

  const dispatch = useDispatch();
  const { query: { employer: employerId } } = router;

  // Set the first name, last name, and email for the fresh desk client
  useEffect(() => {
    if (window && window.fcWidget) {
      window.fcWidget.user.setProperties({
        firstName: profile?.first_name,
        lastName: profile?.last_name,
        email: profile?.email,
      });
    }

    if (isAuthenticated) {
      dispatch(getAgreementAcceptStatus());
    }
  }, [profile]);

  useEffect(() => {
    const loadScript = (url) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      document.getElementsByTagName('head')[0].appendChild(script);
    };
    loadScript(`https://maps.googleapis.com/maps/api/js?key=${config.googlePlacesAPIKey}&libraries=places`);
  }, []);

  useEffect(() => {
    const isSettingsPage = router?.pathname === PAGES.SETTINGS;
    if (employerId && !isSettingsPage) {
      dispatch(getOrganization({ organizationId: employerId }));
    }
  }, [employerId]);

  return (
    <PageContainer isRebrand={isRebrand}>
      <Component {...pageProps} />
      <Notification />
    </PageContainer>
  );
};

const Application = ({ Component, pageProps, store, router }) => {
  const { isMobileUA } = pageProps;
  const isMobile = useIsMobile(768, isMobileUA);
  const isMobileSmall = useIsMobile(351, isMobileUA);
  const isTablet = useIsMobile(1024, isMobileUA);

  useEffect(() => {
    const initialize = async () => {
      openAppStoreIfNeeded(router);
      Router.events.on('routeChangeComplete', () => {
        // any scrolls on the jobs results page are explicitly defined in the features.
        handleRouteChangeComplete();
      });

      Router.events.on('routeChangeStart', (url) => {
        if (!isBackClick) {
          store.dispatch(setHistory(url));
        }

        isBackClick = false;
      });

      Router.beforePopState(() => {
        isBackClick = true;
        store.dispatch(popHistory());
        return true;
      });

      await store.dispatch(authenticationActions.initializeArrivalState());
      await store.dispatch(authenticationActions.initializeProfileStatus());
      await store.dispatch(authenticationActions.setCurrentProfile());
    };

    initialize();

    return () => { Router.events.off('routeChangeComplete', handleRouteChangeComplete); };
  }, []);

  /**
   * Registration hook for the service worker. Needed to intercept WebPush notifications and
   * any PWA functionality.
   */
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js');
      });
    }
    const script = document.createElement('script');

    script.src = '//fw-cdn.com/1960578/2734277.js';
    script.chat = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const appContext = {
    isMobile,
    isMobileSmall,
    isMobileUA,
    isTablet,
  };

  return (
    <ThemeProvider theme={styles}>
      <AppContext.Provider value={appContext}>
        <Head path={router.route} />
        <Provider store={store}>
          <ApolloProvider client={client}>
            <PageContainerWrapper
              Component={Component}
              pageProps={pageProps}
              router={router}
            />
          </ApolloProvider>
        </Provider>
      </AppContext.Provider>
    </ThemeProvider>
  );
};

Application.getInitialProps = async ({ Component, ctx }) => {
  let userAgent;
  const eventId = get(ctx, 'query.id', '');
  const employerId = get(ctx, 'query.employer', '');
  const subEmployerId = get(ctx, 'query.subEmployer', '');

  const isHiringEventsUrl = get(ctx, 'req.url')?.split('?')[0]?.endsWith('hiring-events');

  const store = get(ctx, 'store', {});
  let employer;
  store.dispatch(setRebrand(true));

  if (ctx.isServer) {
    const tokensCookie = getCookie(ctx, config.tokensCookieKey);

    // Attempt to determine if the user is authenticated before rendering the page.
    // Currently this just checks if the user has a cookie with tokens.
    // It does not currently validate that these tokens are valid until the page loads.
    await store.dispatch(authenticationActions.setIsAuthenticated(!!tokensCookie));

    // Set the arrival lock on the server and add to redux state so that it
    // can be the same on the server and the client.
    await store.dispatch(applicationActions.setArrivalLock(ctx.req));

    // Set the browser referer and add to redux state
    await store.dispatch(applicationActions.setBrowserReferer(ctx.req));

    // Set the cloudflare headers and add to redux state
    await store.dispatch(applicationActions.setCloudflareHeaders(ctx.req));

    await store.dispatch(loadAllEmployerAccounts(employerId, subEmployerId, ctx));

    const state = store?.getState();
    employer = state?.entities?.employers?.data?.[employerId];
    const isJobcaseAdmin = state?.features?.employers?.jobcaseEmployerAdminLevel;
    const hasAllEmployersGrant = state?.features?.employers?.grants?.['*'];

    if ((!employer && isJobcaseAdmin) || hasAllEmployersGrant) {
      if (!employerId) {
        employer = await store.dispatch(loadEmployerAccount(JOBCASE_EMPLOYER_ID, ctx));
      } else {
        employer = await store.dispatch(loadEmployerAccount(employerId, ctx));
      }
    }

    // Fetching the employer and setting it active in employer switcher provided as query parameter on event details page
    if ((eventId || isHiringEventsUrl) && employerId) {
      await store.dispatch(employerActions.getEmployersByName({
        name: employer?.name,
      }, {}, ctx));
    }

    userAgent = get(ctx, 'req.headers["user-agent"]', '');
  } else {
    userAgent = get(window, 'navigator.userAgent');
  }

  const isMobileUA = !!userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i);

  const appProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};

  return {
    pageProps: { ...appProps, isMobileUA },
  };
};

export default withRedux(makeStore)(Analytics(Router)(Application));
